import { ArrowDownward, Delete, ExpandMore, FileDownload, Refresh } from '@mui/icons-material'
import {
    Accordion, AccordionDetails, AccordionSummary,
    Box,
    Button, ButtonGroup, ClickAwayListener, Dialog,
    DialogContent, DialogTitle, FormControlLabel, FormLabel, Grid, Grow,
    LinearProgress,
    Link,
    MenuItem, MenuList, Paper, Popper, Radio, RadioGroup, Typography
} from '@mui/material'
import { ArrowDropDownIcon } from '@mui/x-date-pickers'
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { Accessibility, CustomFormField } from '../../component/component'
import { deleteUser, fetchRegistrationList, fetchUserInformation, makeExternalCalls } from '../../service/Service'
import { AppContext } from '../../store/AppProvider'
import { SHOW_ERROR_ALERT, SHOW_SUCCESS_ALERT } from '../../store/reducer/AlertNotifierReducer'
import { ErrorConstant } from '../../utils/Constants'
import { accessibility, convertLocalDateString, FEATURES, isEmptyObj, scrollToElement } from '../../utils/Utils'
import './UserInfoSearch.css'

export const UserInfoSearch = () => {
    const navigate = useNavigate()
    const { name: { displayName }, setToggleNav, setAlert, activeRole } = useContext(AppContext)
    const DELETE_TYPE = {
        other: 'other',
        ccpa_id: 'ccpa_id'
    }
    const {
        handleSubmit: handleSearchSubmit,
        control: searchControl,
        reset: searchReset,
        formState: { errors: searchErrors }
    } = useForm({
        defaultValues: {
            email: "",
            ccpa_id: ""
        }
    })

    const {
        control,
        reset
    } = useForm({
        defaultValues: {
            first_name: '',
            last_name: '',
            email: "",
            phone: "",
            state: "",
            city: "",
            zipcode: "",
        }
    })

    const {
        handleSubmit: handleDeleteSubmit,
        control: deleteControl,
        reset: deleteReset,
        formState: { errors: deleteErrors }
    } = useForm({
        defaultValues: {
            ccpa_id: '',
            delete_reason: '',
        }
    })

    const {
        control: ccpaControl,
        reset: ccpaReset,
    } = useForm({
        defaultValues: {
            delete_by: '',
            delete_user_info: '',
            delete_no_of_records: '',
            delete_reason: '',
            delete_time: '',
        }
    })

    const [optionsObj, setOptionsObj] = useState({})
    const labelOptions = Object.keys(optionsObj)
    const options = Object.values(optionsObj)

    const [userDetails, setUserDetails] = useState({})

    const [searchEmail, setSearchEmail] = useState('')

    const [showTable, setShowTable] = useState(false)
    const [showLoading, setShowLoading] = useState(false)
    const [tableData, setTableData] = useState([])
    const [totalRecordCount, setTotalRecordCount] = useState(0)
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 25,
    });
    const [sorting, setSorting] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showCCPA, setShowCCPA] = useState(false)
    const [selectedDltField, setSelectedDltField] = useState(DELETE_TYPE.ccpa_id)
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [newCCPAID, setNewCCPAID] = useState(null);

    useEffect(() => {
        const configureOptions = () => {
            let opt = {}
            if (accessibility(FEATURES.VIEW_USER_INFO, activeRole)) {
                opt['EMAIL'] = 'SEARCH BY EMAIL ID'
            }
            if (accessibility(FEATURES.VIEW_CCPA_DETAILS, activeRole)) {
                opt['CCPA-ID'] = 'SEARCH BY CCPA_ID'
            }
            setOptionsObj(opt)
        }
        configureOptions()
    }, [activeRole])

    const handleMenuItemClick = (event, index) => {
        setSelectedIndex(index);
        setOpen(false);
        searchReset()
        clearPage()
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const navigateToDetailsPage = useCallback(({ registration_id }, enableEdit = false) => {
        setToggleNav(false);
        navigate(`${registration_id}/details`, { state: { registrationId: registration_id, enableEdit } })
    }, [setToggleNav, navigate])

    const columns = useMemo(
        () => [
            {
                accessorKey: 'registration_id',
                header: 'Registration Id',
                size: 150,
                Cell: ({ row: { original } }) => {
                    return accessibility(FEATURES.VIEW_REGISTRATIONS_DETAILS, activeRole)
                        ? <Link component="button" onClick={() => navigateToDetailsPage(original)}>
                            {original?.registration_id}
                        </Link>
                        : original?.registration_id
                }
            },
            {
                accessorKey: 'user_name',
                header: 'User Name',
                size: 130,
                accessorFn: (row) => `${row.first_name} ${row.last_name}`
            },
            {
                accessorKey: 'registration_serial_number',
                header: 'Registration Serial Number',
                size: 150,
            },
            {
                accessorKey: 'sku_name',
                header: 'Sku Name',
                size: 150,
            },
            {
                accessorKey: 'registration_date',
                header: 'Registration Date',
                size: 150,
            },
            {
                accessorKey: 'purchase_date',
                header: 'Purchase Date',
                size: 150,
            },
            {
                accessorKey: 'date_of_delivery',
                header: 'Date of Delivery',
                size: 150,
            },
            {
                accessorKey: 'registration_active',
                header: 'Active',
                size: 150,
            },

            {
                accessorKey: 'retailer_name',
                header: 'Retailer Name',
                size: 150,
            },
            {
                accessorKey: 'registration_warranty_end_date',
                header: 'Registration Warranty End Date',
                size: 210,
            },
            {
                accessorKey: 'registration_manufacture_warranty_end_date',
                header: 'Manufacture Warranty End Date',
                size: 250,
            },
            {
                accessorKey: 'campaign_id',
                header: 'Campaign Id',
                size: 150,
            },
            {
                accessorKey: 'source',
                header: 'Source',
                size: 150,
            },
            {
                accessorKey: 'locale',
                header: 'Locale',
                size: 150,
            },
        ],
        [navigateToDetailsPage, activeRole],
    );

    const handleExportRegData = async (column) => {
        try {
            const fetchedData = await fetchRegistrationList({ email: searchEmail, ...pagination, isDownload: true });
            window.open(fetchedData, "_blank");
        } catch (e) {
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.downloadingCSV })
        }
    }

    const onSearch = useCallback(async (formData) => {
        try {
            setShowLoading(true);
            setTableData([]);
            const registeredProducts = await fetchRegistrationList({ ...formData, ...pagination, sorting });
            setTableData(registeredProducts?.data)
            setTotalRecordCount(registeredProducts?.totalCount)
            scrollToElement('search_table')
            setShowTable(true)
            setShowLoading(false)
        } catch (error) {
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.fetchRegisteredProducts })
            setTableData([])
            setShowTable(false)
        }
    }, [pagination, sorting, setAlert])

    const handleSearch = useCallback(async (formData) => {
        try {
            setShowLoading(true)
            if (showCCPA) {
                setShowCCPA(false)
                ccpaReset()
            }
            if (Boolean(newCCPAID)) {
                setNewCCPAID('')
            }
            if (!isEmptyObj(userDetails)) {
                setUserDetails({});
                setTableData([]);
                setShowTable(false)
            }
            const email = formData?.email?.trim()?.toLowerCase() || ''
            setSearchEmail(email)
            const ccpa_id = formData?.ccpa_id?.trim()?.toLowerCase() || ''
            const userInfo = await fetchUserInformation({ email, ccpa_id });
            if (labelOptions[selectedIndex]?.toLowerCase() === 'ccpa-id') {
                if (!isEmptyObj(userInfo)) {
                    const { number_of_records, first_name, last_name, remarks, deleted_user_email, tracking_date_time, admin_email } = userInfo
                    ccpaReset({
                        delete_no_of_records: number_of_records,
                        delete_reason: remarks,
                        delete_user_info: `${first_name} ${last_name} (${deleted_user_email})`,
                        delete_time: convertLocalDateString(tracking_date_time),
                        delete_by: admin_email
                    })
                    setShowCCPA(true)
                } else {
                    setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.fetchCCPADetails })
                    setShowCCPA(false)
                }
            } else if (labelOptions[selectedIndex]?.toLowerCase() === 'email') {
                reset({
                    first_name: userInfo?.first_name || "",
                    last_name: userInfo?.last_name || "",
                    email: userInfo?.email || "",
                    phone: userInfo?.phone || "",
                    state: userInfo?.state || "",
                    city: userInfo?.city || "",
                    zipcode: userInfo?.zipcode || "",
                })
                if (!isEmptyObj(userInfo)) {
                    setUserDetails(userInfo)
                    onSearch({ ...formData })
                } else {
                    setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.noUserAvailable })
                }
            }
        } catch (error) {
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.fetchRegisteredProducts })
        } finally {
            setShowLoading(false)
        }
    }, [showCCPA, labelOptions, selectedIndex, ccpaReset, setAlert, reset, onSearch, newCCPAID, userDetails])

    const onError = () => { }

    const handleSearchReset = () => {
        searchReset()
        clearPage()
    }

    const clearPage = () => {
        if (!isEmptyObj(userDetails)) setUserDetails({})
        if (tableData?.length) {
            setTableData([])
            setShowTable(false)
            setTotalRecordCount(0)
        }
        if (showCCPA) setShowCCPA(false)
    }

    const handleUserDelete = async (formData) => {
        try {
            setShowDeleteModal(false)
            setShowLoading(true)
            const { ccpa_id, delete_reason } = formData
            const { token, new_ccpa_id } = await deleteUser({ email: userDetails?.email, ccpa_id, delete_reason, type: selectedDltField })
            if (token) {
                const delete_no_of_records = totalRecordCount
                const delete_user_info = `${userDetails?.first_name} ${userDetails?.last_name} (${userDetails?.email})`
                clearPage()
                searchReset()
                deleteReset()
                ccpaReset({
                    delete_no_of_records,
                    delete_reason,
                    delete_user_info,
                    delete_time: convertLocalDateString(new Date()),
                    delete_by: displayName
                })
                setShowCCPA(true)
                if (Boolean(new_ccpa_id)) setNewCCPAID(new_ccpa_id)
                makeExternalCalls(token)
            }
            setAlert({ type: SHOW_SUCCESS_ALERT, alertMessage: "User deleted successfully" })
        } catch (error) {
            setAlert({ type: SHOW_ERROR_ALERT, alertMessage: ErrorConstant.deleteUser })
        } finally {
            setShowLoading(false)
            deleteReset();
            setSelectedDltField(DELETE_TYPE.ccpa_id)
        }
    }

    const table = useMaterialReactTable({
        columns,
        data: tableData,
        enableStickyHeader: true,
        positionGlobalFilter: 'right',
        // manualFiltering: false,
        enableGlobalFilter: false,
        // onGlobalFilterChange: setGlobalFilter,
        showHideFilters: false,
        // enableTopToolbar: true,
        enableColumnFilter: false,
        enableHiding: true, //this shows the hide/ show columns
        // enableFullScreenToggle: true,
        enableColumnActions: false,
        enableDensityToggle: false,
        //  enableFilters: false, //this shows the search icon and filter icon together
        enableColumnFilters: false, //this hides the column filter
        muiSearchTextFieldProps: {
            placeholder: `Search by hardware SKU`,
            sx: { minWidth: '300px' },
            variant: 'outlined',
        },
        muiTableContainerProps: { sx: { maxHeight: '400px' } },
        initialState: {
            // columnVisibility: { action: false },
            columnPinning: { left: ['registration_id'], right: ['action'] },
            density: 'comfortable',
        },
        muiTableHeadCellProps: {
            align: 'center',
            //simple styling with the `sx` prop, works just like a style prop in this example
            sx: {
                backgroundColor: '#1976d2',
                color: 'white',
                fontSize: '14px',
                verticalAlign: 'middle',
            },
        },
        muiColumnActionsButtonProps: ({ column, table }) => (<ArrowDownward />),
        muiTableBodyCellProps: {
            align: 'center',
            sx: {
                borderRight: '1px solid #e0e0e0',
                verticalAlign: 'middle',
            }
        },

        renderTopToolbarCustomActions: ({ table }) => (
            <Box
                sx={{
                    display: 'flex',
                    gap: '16px',
                    flexWrap: 'wrap',
                }}
            >
                <Button
                    //export all data that is currently in the table (ignore pagination, sorting, filtering, etc.)
                    onClick={() => handleExportRegData(table?.getVisibleFlatColumns())}
                    startIcon={<FileDownload />}
                >
                    Export CSV
                </Button>
            </Box>
        ),
        manualPagination: true,
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        paginationDisplayMode: 'pages',
        rowCount: totalRecordCount, //should be the total records coming from response
        muiPaginationProps: {
            showRowsPerPage: false,
            shape: 'rounded',
            showFirstButton: true,
            showLastButton: true,
        },
        state: {
            pagination,
            sorting,
            // globalFilter
        },
    });
    const selectedField = labelOptions?.[selectedIndex]?.toLowerCase() === 'email' ? 'email' : 'ccpa_id'
    const hasSearchFieldError = Boolean(searchErrors?.[`${selectedField}`]?.message)
    const deleteByCCPAID = selectedDltField === 'ccpa_id'

    const dataRef = useRef(tableData);

    useEffect(() => {
    dataRef.current = tableData;
    }, [tableData]);

    useEffect(() => {
        // Don't fetch until initial data exists
        if (!dataRef?.current?.length) return;
        onSearch({ email: searchEmail });
        console.log(sorting);
    }, [pagination, sorting, onSearch, searchEmail]);
    

    return (
        <div>
            <div className='header-title' style={{ letterSpacing: '0.5px' }}>{('User Info Search').toUpperCase()}</div>
            <form
                onSubmit={handleSearchSubmit(handleSearch, onError)}
                noValidate
                autoComplete="off">
                <Grid container columnSpacing={2.5} marginBottom={3} alignItems={hasSearchFieldError ? 'center' : 'flex-end'}>
                    <Grid item xs={12} md={5}>
                        <CustomFormField
                            label={labelOptions[selectedIndex]}
                            control={searchControl}
                            name={selectedField}
                            errorMessage={searchErrors?.[`${selectedField}`]?.message}
                            rules={{
                                required: `${labelOptions?.[selectedIndex]?.toLowerCase()} is required`,
                                pattern: labelOptions?.[selectedIndex]?.toLowerCase() === 'email'
                                    ? {
                                        value: /\S+@\S+\.\S+/,
                                        message: "Entered value does not match email format",
                                    } : {}
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={3} marginBottom={hasSearchFieldError ? -0.4 : 0.4}>
                        <>
                            <ButtonGroup
                                variant="contained"
                                ref={anchorRef}
                                aria-label="Button group with a nested menu"
                                style={{ width: '100%' }}
                            >
                                <Button style={{ width: '100%' }} type="submit">{options[selectedIndex]}</Button>
                                <Button
                                    size="small"
                                    aria-controls={open ? 'split-button-menu' : undefined}
                                    aria-expanded={open ? 'true' : undefined}
                                    aria-label="select merge strategy"
                                    aria-haspopup="menu"
                                    onClick={handleToggle}
                                >
                                    <ArrowDropDownIcon />
                                </Button>
                            </ButtonGroup>
                            <Popper
                                sx={{
                                    zIndex: 1,
                                }}
                                open={open}
                                anchorEl={anchorRef.current}
                                role={undefined}
                                transition
                                disablePortal
                            >
                                {({ TransitionProps, placement }) => (
                                    <Grow
                                        {...TransitionProps}
                                        style={{
                                            transformOrigin:
                                                placement === 'bottom' ? 'center top' : 'center bottom',
                                        }}
                                    >
                                        <Paper>
                                            <ClickAwayListener onClickAway={handleClose}>
                                                <MenuList id="split-button-menu" autoFocusItem>
                                                    {options.map((option, index) => (
                                                        <MenuItem
                                                            key={option}
                                                            selected={index === selectedIndex}
                                                            onClick={(event) => handleMenuItemClick(event, index)}
                                                        >
                                                            {option}
                                                        </MenuItem>
                                                    ))}
                                                </MenuList>
                                            </ClickAwayListener>
                                        </Paper>
                                    </Grow>
                                )}
                            </Popper>
                        </>
                    </Grid>
                    <Grid item xs={12} md={2} marginBottom={hasSearchFieldError ? -0.4 : 0.4}>
                        <Button startIcon={<Refresh />} variant="outlined" size="medium" style={{ width: '100%', backgroundColor: 'white' }} onClick={() => handleSearchReset()}>Reset</Button >
                    </Grid>
                    <Grid item xs={12} md={2} marginBottom={hasSearchFieldError ? -0.4 : 0.4}>
                        <Accessibility feature={FEATURES.DELETE_USER_INFO}>
                            <Button disabled={isEmptyObj(userDetails)} style={{ width: '100%' }} variant='contained' color='error' onClick={() => setShowDeleteModal(true)} startIcon={<Delete />} >Delete</Button>
                        </Accessibility>
                    </Grid>
                </Grid>
            </form>
            {!isEmptyObj(userDetails) && !showCCPA &&
                <Accordion defaultExpanded style={{ marginTop: 40 }}>
                    <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'white', fontSize: 30 }} />} sx={{ backgroundColor: '#1976d2', maxHeight: 38, '&.Mui-expanded': { backgroundColor: '#1976d2', minHeight: 48 } }}>
                        <Typography variant="h6" style={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>{('User Details').toUpperCase()}</Typography>
                    </AccordionSummary>
                    <AccordionDetails style={{ marginTop: 20 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Grid container alignItems="center" justifyContent="left" spacing={1}>
                                    <Grid item xs={4.5}>
                                        <Typography style={{ fontWeight: 600 }} >First Name :</Typography>
                                    </Grid>
                                    <Grid item xs={7.5}>
                                        <CustomFormField control={control} name={"first_name"} editMode={false} readOnly />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item xs={4.5}>
                                        <Typography style={{ fontWeight: 600 }}>Last Name :</Typography>
                                    </Grid>
                                    <Grid item xs={7.5}>
                                        <CustomFormField control={control} name={"last_name"} editMode={false} readOnly />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item xs={4.5}>
                                        <Typography style={{ fontWeight: 600 }}>Email :</Typography>
                                    </Grid>
                                    <Grid item xs={7.5}>
                                        <CustomFormField
                                            control={control}
                                            name={"email"}
                                            editMode={false}
                                            readOnly
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item xs={4.5}>
                                        <Typography style={{ fontWeight: 600 }}>Phone :</Typography>
                                    </Grid>
                                    <Grid item xs={7.5}>
                                        <CustomFormField
                                            control={control}
                                            name={"phone"}
                                            editMode={false}
                                            readOnly />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item xs={4.5}>
                                        <Typography style={{ fontWeight: 600 }}>City :</Typography>
                                    </Grid>
                                    <Grid item xs={7.5}>
                                        <CustomFormField
                                            control={control}
                                            name={"city"}
                                            editMode={false}
                                            readOnly />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item xs={4.5}>
                                        <Typography style={{ fontWeight: 600 }}>State :</Typography>
                                    </Grid>
                                    <Grid item xs={7.5}>
                                        <CustomFormField
                                            control={control}
                                            name={"state"}
                                            editMode={false}
                                            readOnly />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item xs={4.5}>
                                        <Typography style={{ fontWeight: 600 }}>zipcode :</Typography>
                                    </Grid>
                                    <Grid item xs={7.5}>
                                        <CustomFormField
                                            control={control}
                                            name={"zipcode"}
                                            editMode={false}
                                            readOnly
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>}

            {showCCPA &&
                <Accordion defaultExpanded style={{ marginTop: 40 }}>
                    <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'white', fontSize: 30 }} />}
                        sx={{ backgroundColor: '#c70000', maxHeight: 38, '&.Mui-expanded': { backgroundColor: '#c70000', minHeight: 48 } }}>
                        <Typography variant="h6" style={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>{('Deleted User Details').toUpperCase()}</Typography>
                    </AccordionSummary>
                    <AccordionDetails >
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={12} display={"flex"} justifyContent={"center"} >
                                {Boolean(newCCPAID)
                                    ? <div className='ccpa_id_note'>**Note: An autogenerated CCPA-ID:&nbsp;<strong>{newCCPAID}</strong>&nbsp;has been created for current deletion. Please note this for future enquiry!</div>
                                    : <div />}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Grid container alignItems="center" justifyContent="left" spacing={1}>
                                    <Grid item xs={4.5}>
                                        <Typography style={{ fontWeight: 600 }} >Deleted By :</Typography>
                                    </Grid>
                                    <Grid item xs={7.5}>
                                        <CustomFormField control={ccpaControl} name={"delete_by"} editMode={false} readOnly />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item xs={4.5}>
                                        <Typography style={{ fontWeight: 600 }}>Deleted User Info :</Typography>
                                    </Grid>
                                    <Grid item xs={7.5}>
                                        <CustomFormField control={ccpaControl} name={"delete_user_info"} editMode={false} readOnly />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item xs={4.5}>
                                        <Typography style={{ fontWeight: 600 }}>No. of deleted Registered Records :</Typography>
                                    </Grid>
                                    <Grid item xs={7.5}>
                                        <CustomFormField
                                            control={ccpaControl}
                                            name={"delete_no_of_records"}
                                            editMode={false}
                                            readOnly
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item xs={4.5}>
                                        <Typography style={{ fontWeight: 600 }}>Delete Reason :</Typography>
                                    </Grid>
                                    <Grid item xs={7.5}>
                                        <CustomFormField
                                            control={ccpaControl}
                                            name={"delete_reason"}
                                            editMode={false}
                                            readOnly />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item xs={4.5}>
                                        <Typography style={{ fontWeight: 600 }}>Deletion time :</Typography>
                                    </Grid>
                                    <Grid item xs={7.5}>
                                        <CustomFormField
                                            control={ccpaControl}
                                            name={"delete_time"}
                                            editMode={false}
                                            readOnly />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>}
            <div id={"search_table"} style={{ marginTop: 25 }}>
                {showLoading && <LinearProgress />}
                {showTable &&
                    <MaterialReactTable
                        table={table}
                    />}
            </div>
            <Dialog
                open={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth
                maxWidth='sm'
            >
                <DialogTitle id="alert-dialog-title">
                    Delete Confirmation
                </DialogTitle>
                <DialogContent>
                    <form
                        onSubmit={handleDeleteSubmit(handleUserDelete, onError)}
                        noValidate
                        autoComplete="off">
                        <Grid item xs={12} md={12}>
                            <FormLabel id="demo-row-radio-buttons-group-label">Delete By</FormLabel>
                            <RadioGroup
                                row
                                aria-labelledby="demo-row-radio-buttons-group-label"
                                name="row-radio-buttons-group"
                            >
                                <FormControlLabel value={DELETE_TYPE.ccpa_id} control={<Radio />} label="CCPA-ID" checked={deleteByCCPAID} onChange={() => setSelectedDltField(DELETE_TYPE.ccpa_id)} />
                                <FormControlLabel value={DELETE_TYPE.other} control={<Radio />} label="Other" checked={selectedDltField === DELETE_TYPE.other} onChange={() => setSelectedDltField(DELETE_TYPE.other)} />
                            </RadioGroup>
                        </Grid>
                        <Grid item xs={12} md={12}>
                            {deleteByCCPAID
                                ? <CustomFormField control={deleteControl} name={"ccpa_id"}
                                    label='CCPA ID'
                                    rules={{
                                        required: deleteByCCPAID && 'CCPA-ID is Required',
                                        maxLength: deleteByCCPAID ? { value: 10, message: "max length 10 exceeded" } : {}
                                    }}
                                    errorMessage={deleteErrors?.ccpa_id?.message} />
                                : <div className='ccpa_id_note'>**Note: An autogenerated <strong>CCPA-ID</strong> will be created post deletion. Please note that for future enquiry!</div>}
                        </Grid>
                        <Grid item xs={12} md={12}>
                            <CustomFormField control={deleteControl} name={"delete_reason"}
                                label='Delete Reason'
                                rules={{
                                    required: 'Delete Reason is Required',
                                    maxLength: { value: 100, message: "max length 100 exceeded" }
                                }}
                                errorMessage={deleteErrors?.delete_reason?.message} />
                        </Grid>
                        <div className='delete_confirmation'>
                            <Button variant='contained' type='submit'>Yes</Button>
                            <Button variant='outlined' style={{ marginLeft: 10 }} onClick={() => { deleteReset(); setShowDeleteModal(false) }} autoFocus>No</Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    )
}
